import graphql from "babel-plugin-relay/macro";
import { useState } from "react";
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
} from "react-native";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";

import { Container } from "@whoosh/design-system/src/apps/PatronApp/layout/Container";
import {
  Box,
  HStack,
  ScrollView,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { useBreakpointValue } from "@whoosh/design-system/src/hooks/useBreakpointValue";

import { useInitEventForm } from "../../atoms/useInitEventForm";
import { useInitEventMeta } from "../../atoms/useInitEventMeta";
import { useInitEventSignUps } from "../../atoms/useInitEventSignUps";
import { useInitGroupEvent } from "../../atoms/useInitGroupEvent";
import { EventDetailBody } from "../EventDetailBody";
import { EventDetailHeader } from "../EventDetailHeader";
import { EventDetailJoinEventSmall } from "../EventDetailJoinEvent/EventDetailJoinEvent.small";
import { EventDetailSignUpSection } from "../EventDetailSignUpSection";
import { UpdateAttendeesGroupEventsModal } from "../UpdateAttendeesGroupEvents";
import { EventDetailShellQuery } from "./__generated__/EventDetailShellQuery.graphql";

type EventDetailShellProps = {
  queryRef: PreloadedQuery<EventDetailShellQuery>;
};

export const EventDetailShell = ({ queryRef }: EventDetailShellProps) => {
  const data = usePreloadedQuery(EventDetailQueryString, queryRef);
  const event = data.node?.__typename === "Event" ? data.node : null;

  useInitGroupEvent({ fragmentRef: data.node ?? null });

  useInitEventMeta({ fragmentRef: data.node ?? null });

  useInitEventForm({ fragmentRef: data.node ?? null });

  useInitEventSignUps({ fragmentRef: data.node ?? null });

  const [isScrolled, setIsScrolled] = useState(false);

  const onScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const offsetY = event.nativeEvent.contentOffset.y;
    setIsScrolled(offsetY > 50);
  };

  const isSmallScreen = useBreakpointValue({
    base: true,
    xs: true,
    sm: true,
    md: true,
    lg: false,
  });

  return data.node ? (
    <>
      <Container
        marginTop={0}
        paddingHorizontal={{ base: 0, md: 16 }}
        height="100%"
        flex={1}
      >
        {/* Header on mobile */}
        <Box visible={{ xs: true, lg: false }}>
          <EventDetailHeader isScrolled={isScrolled} fragmentRef={data.node} />
        </Box>
        {/* Header on mobile end */}

        <ScrollView
          flex={1}
          height="100%"
          onScroll={onScroll}
          scrollEventThrottle={16}
          pb={{ base: 0, lg: 10 }}
          style={Platform.OS === "android" ? { paddingBottom: 120 } : undefined}
        >
          <Box width="100%" maxWidth={1240} alignSelf="center">
            <HStack
              flexDirection={{ base: "column", md: "column", lg: "row" }}
              gap={{ base: 0, lg: 24 }}
            >
              {/* Left Column */}
              <Box flex={{ lg: 1 }} width={{ base: "100%", lg: "auto" }}>
                {/* Header on desktop */}
                <Box
                  visible={{
                    xs: false,
                    base: false,
                    sm: false,
                    md: false,
                    lg: true,
                  }}
                >
                  <EventDetailHeader
                    isScrolled={isScrolled}
                    fragmentRef={data.node}
                  />
                </Box>
                {/* Header on desktop end */}

                <Box
                  width={{ base: "100%", lg: "auto" }}
                  px={{ base: 6, lg: 0 }}
                >
                  <EventDetailBody fragmentRef={data.node} />
                </Box>
              </Box>

              {/* Right Column */}
              <Box
                flex={{ lg: 1 }}
                width={{ base: "100%", lg: "auto" }}
                pt={{ base: 6, lg: 0 }}
              >
                <VStack gap={4} flex={1}>
                  <EventDetailSignUpSection fragmentRef={data.node} />
                </VStack>
              </Box>
            </HStack>
          </Box>
        </ScrollView>
        {isSmallScreen ? (
          <EventDetailJoinEventSmall fragmentRef={data.node} />
        ) : null}
        {event ? (
          <UpdateAttendeesGroupEventsModal
            eventId={event.id}
            fragmentRef={event}
            unknownGuestsAllowed={event.unknownGuestsAllowed || false}
          />
        ) : null}
      </Container>
    </>
  ) : null;
};

export const EventDetailQueryString = graphql`
  query EventDetailShellQuery($eventID: ID!, $memberId: ID) {
    session {
      user {
        ... on MemberUser {
          squareCards {
            edges {
              node {
                id
                default
              }
            }
          }
        }
      }
      currentClientProfile {
        member {
          ... on Member {
            id
          }
          ... on Customer {
            id
          }
        }
      }
    }
    node(id: $eventID) {
      id
      __typename
      ... on Event {
        id
        unknownGuestsAllowed
        ...UpdateAttendeesGroupEventsModal_event
        ...EventDetailSignUpSection_event
        ...EventDetailHeader
        ...EventDetailRedesignedHeader
        ...EventDetailBody
        ...NewEventDetailBody
        ...useInitEventMeta_event
        ...useInitGroupEvent_event
        ...useInitEventSignUps_event
        ...useInitEventForm_formQuestionConfiguration
        ...EventDetailJoinEventSmall_event
        ...EventDetailTitleRow
        ...EventDetailSignUpCard_event
      }
    }
  }
`;
